import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface TutorialSlide {
  title: string;
  content: React.ReactNode;
  image: string;
}

interface TutorialSlideShowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TutorialSlideShow: React.FC<TutorialSlideShowProps> = ({
  open,
  onOpenChange,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);

  // Check if the user has seen the tutorial before
  useEffect(() => {
    const tutorialSeen = localStorage.getItem('tutorialSeen');
    if (tutorialSeen) {
      setHasSeenTutorial(true);
    }
  }, []);

  // Mark tutorial as seen when closed
  const handleClose = () => {
    // We don't set tutorialSeen here anymore, as we want to allow users to reopen the tutorial
    onOpenChange(false);
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const slides: TutorialSlide[] = [
    {
      title: "Welcome to ServicePane",
      content: (
        <div className="space-y-4">
          <p>
            This tutorial will guide you through the basic features of ServicePane,
            helping you create glass estimates quickly and efficiently.
          </p>
          <p>
            Let's get started with a tour of the main features!
          </p>
        </div>
      ),
      image: "/tutorial/welcome.svg"
    },
    {
      title: "Glass Types & Pricing",
      content: (
        <div className="space-y-4">
          <p>
            Start by clicking on the <strong>Glass Types & Pricing</strong> tab at the top of the screen.
          </p>
          <p>
            Here you'll find a variety of glass types organized by category. Click on any glass type to add it to your estimate.
          </p>
        </div>
      ),
      image: "/tutorial/glass-types-tab.svg"
    },
    {
      title: "Selecting a Glass Type",
      content: (
        <div className="space-y-4">
          <p>
            Click on any glass type card to open the details modal.
          </p>
          <p>
            Here you can specify:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Measurement Type (DLO or Glass Size)</li>
            <li>Frame Type (for applicable glass)</li>
            <li>Dimensions (width and height)</li>
            <li>Quantity</li>
          </ul>
        </div>
      ),
      image: "/tutorial/glass-type-modal.svg"
    },
    {
      title: "Adding to Estimate",
      content: (
        <div className="space-y-4">
          <p>
            After entering the dimensions and quantity, click <strong>Add to Estimate</strong> to add the item to your current estimate.
          </p>
          <p>
            The app will automatically calculate the proper glass size based on your measurement type and frame selections.
          </p>
        </div>
      ),
      image: "/tutorial/add-to-estimate.svg"
    },
    {
      title: "Creating an Estimate",
      content: (
        <div className="space-y-4">
          <p>
            Switch to the <strong>Estimate</strong> tab to view your current estimate.
          </p>
          <p>
            Here you'll see all the items you've added, along with calculated dimensions, area, weight, and pricing.
          </p>
          <p>
            You can add more items by returning to the Glass Types tab, or remove items using the trash icon.
          </p>
        </div>
      ),
      image: "/tutorial/estimate-tab.svg"
    },
    {
      title: "Saving Your Estimate",
      content: (
        <div className="space-y-4">
          <p>
            To save your estimate for later:
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Enter a name for your estimate in the "Estimate Name" field</li>
            <li>Click the <strong>Save Estimate</strong> button</li>
          </ol>
          <p>
            Your estimate will be saved locally and can be accessed from the Saved Estimates tab.
          </p>
        </div>
      ),
      image: "/tutorial/save-estimate.svg"
    },
    {
      title: "Viewing Saved Estimates",
      content: (
        <div className="space-y-4">
          <p>
            Switch to the <strong>Saved Estimates</strong> tab to view all your saved estimates.
          </p>
          <p>
            From here you can:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>View the details of any saved estimate</li>
            <li>Download a PDF of the estimate</li>
            <li>Delete estimates you no longer need</li>
          </ul>
        </div>
      ),
      image: "/tutorial/saved-estimates.svg"
    },
    {
      title: "Downloading PDF Estimates",
      content: (
        <div className="space-y-4">
          <p>
            To download a PDF of your estimate:
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Go to the Saved Estimates tab</li>
            <li>Find the estimate you want to download</li>
            <li>Click the green paper icon (download button)</li>
          </ol>
          <p>
            The PDF will include all item details, dimensions, and pricing information.
          </p>
        </div>
      ),
      image: "/tutorial/download-pdf.svg"
    },
    {
      title: "You're All Set!",
      content: (
        <div className="space-y-4">
          <p>
            You now know the basics of using ServicePane to create and manage glass estimates.
          </p>
          <p>
            Click "Finish" to start using the app, or go back to review any steps.
          </p>
          <p className="text-sm text-muted-foreground">
            You can access this tutorial again from the help menu if needed.
          </p>
        </div>
      ),
      image: "/tutorial/complete.svg"
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => isOpen ? onOpenChange(true) : handleClose()}>
      <DialogContent className="max-w-4xl bg-[#0f172a] text-white border-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#60a5fa] text-2xl">
            {slides[currentSlide].title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div className="space-y-4">
            {slides[currentSlide].content}
            <div className="text-sm text-muted-foreground mt-4">
              Step {currentSlide + 1} of {slides.length}
            </div>
          </div>
          <div className="bg-[#1e293b] rounded-lg overflow-hidden border border-[#4b5563] shadow-lg">
            <img
              src={slides[currentSlide].image}
              alt={`Tutorial step ${currentSlide + 1}`}
              className="w-full h-auto object-contain max-h-[300px]"
            />
          </div>
        </div>

        <DialogFooter className="mt-6 flex flex-col sm:flex-row justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSlide === 0}
            className="text-white border-[#4b5563] hover:bg-[#243447] hover:text-white w-full sm:w-auto"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <Button
            onClick={handleNext}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            {currentSlide === slides.length - 1 ? 'Finish' : 'Next'}
            {currentSlide < slides.length - 1 && <ChevronRight className="h-4 w-4 ml-2" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialSlideShow;
